/**
 * 结算材料分类配置（核心配置文件）
 * 
 * 说明：
 * - 前端根据此配置动态渲染费用分类卡槽
 * - 新增费用类型只需在此文件添加配置，零代码改动
 * - legacyCode 对应老系统 projectTree.json 中的 spendingProjectCode
 * - auditRules 对应后端 RuleEngine 中的规则编码
 * 
 * 结构：FeeCategory[] → DocSlot[] → AuditRule[]
 */

// ========================
// 文件格式常量
// ========================
const ACCEPT = {
  PDF: '.pdf',
  IMAGE: '.jpg,.jpeg,.png,.bmp',
  PDF_IMAGE: '.pdf,.jpg,.jpeg,.png,.bmp',
  EXCEL: '.xlsx,.xls',
  PDF_EXCEL: '.pdf,.xlsx,.xls',
  DOC: '.pdf,.doc,.docx,.ppt,.pptx',
  ALL: '.pdf,.jpg,.jpeg,.png,.bmp,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.zip,.rar,.txt',
  VIDEO_LINK: '.mp4,.avi,.mov,.txt', // txt用于粘贴视频链接
}

// ========================
// 费用大类配置
// ========================
export const FEE_CATEGORIES = [
  // =====================================================
  // 1. 综合材料 — 每单必传（默认展开）
  // =====================================================
  {
    code: 'GENERAL',
    label: '综合材料',
    legacyCode: 'UM000', // 老系统 code
    icon: 'el-icon-folder-opened',
    alwaysRequired: true,  // 每单必传
    defaultExpanded: true, // 默认展开
    slots: [
      {
        id: 'CONTRACT',
        label: '合作协议（双签）',
        required: true,
        accept: ACCEPT.PDF,
        tip: '双签协议（需双方盖公章或合同专用章，两页以上需加盖骑缝章）',
        auditRules: ['CONTRACT_PARTY_MATCH', 'CONTRACT_AMOUNT_MATCH', 'CONTRACT_INTEGRITY', 'CONTRACT_SEAL'],
      },
      {
        id: 'INVOICE_MAIN',
        label: '增值税发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（需盖章，清晰完整）',
        auditRules: ['INVOICE_AMOUNT_MATCH', 'INVOICE_SEAL', 'INVOICE_DATE', 'INVOICE_TAX_RATE', 'INVOICE_TYPE_MATCH_CONTRACT', 'INVOICE_TOTAL_MATCH_SETTLEMENT', 'INVOICE_VERIFICATION'],
      },
      {
        id: 'SETTLEMENT_SHEET',
        label: '结算单（盖章版）',
        required: true,
        accept: ACCEPT.PDF,
        tip: '基于报价出具的实际费用结算单（盖章原件）',
        auditRules: ['SETTLEMENT_COMPREHENSIVE', 'SETTLEMENT_FORBIDDEN_WORD'],
      },
    ],
  },

  // =====================================================
  // 2. 基础资料 — 每单必传（默认展开）
  // =====================================================
  {
    code: 'BASIC_INFO',
    label: '基础资料（通知 / 日程 / 签到 / 照片）',
    legacyCode: 'UM000', // 基础资料也归属在 UM000 综合必传里
    icon: 'el-icon-document',
    alwaysRequired: true,
    defaultExpanded: true,
    slots: [
      {
        id: 'NOTICE',
        label: '会议通知函',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '体现会议时间、地点、会议主题',
        auditRules: ['NOTICE_PROJECT_NAME_MATCH', 'NOTICE_SEAL_REQUIRED', 'NOTICE_TIME_MATCH'],
      },
      {
        id: 'AGENDA',
        label: '会议日程',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '体现会议时间、地点、主题、时段及安排',
        auditRules: ['AGENDA_INFO_COMPLETE', 'AGENDA_THEME_CONSISTENT', 'AGENDA_VENUE_APPROPRIATE', 'AGENDA_NO_COMPANY_PERSONNEL'],
      },
      {
        id: 'SIGNIN_SHEET',
        label: '签到表',
        required: true,
        accept: ACCEPT.PDF_EXCEL,
        tip: '体现会议时间、地点、主题、参会人员姓名等基本信息',
        conditions: { meetingMode: 'OFFLINE' },
        auditRules: ['SIGNIN_SHEET_COMPREHENSIVE'],
      },
      {
        id: 'SCENE_PHOTO',
        label: '现场照片',
        required: true,
        accept: ACCEPT.IMAGE,
        minCount: 3,
        tip: '至少3张：含会议主题背景、会场全景、参会者',
        conditions: { meetingMode: 'OFFLINE' },
        auditRules: ['PHOTO_COUNT', 'PHOTO_DATE_CONTENT'],
      },
      {
        id: 'ONLINE_SCREENSHOT',
        label: '会议截图 / 回放链接',
        required: true,
        accept: ACCEPT.ALL,
        tip: '线上会议截图或长期有效的视频回放链接',
        conditions: { meetingMode: 'ONLINE' },
        auditRules: [],
      },
      {
        id: 'ONLINE_ATTENDANCE',
        label: '线上参会记录',
        required: true,
        accept: ACCEPT.PDF_EXCEL,
        tip: '平台导出的上线明细数据表',
        conditions: { meetingMode: 'ONLINE' },
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 3. 劳务费 — 按需展开
  // =====================================================
  {
    code: 'LABOR_FEE',
    label: '劳务费（供应商支付）',
    legacyCode: '1',
    icon: 'el-icon-money',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'LABOR_DETAIL',
        label: '劳务费明细表',
        required: true,
        accept: ACCEPT.PDF_EXCEL,
        tip: '明细表体现：姓名、电话、身份证号、开户行、银行卡号、税前/税费/税后金额，加盖章（原件）',
        auditRules: [],
      },
      {
        id: 'SPEAKER_PHOTO',
        label: '讲者照片',
        required: true,
        accept: ACCEPT.IMAGE,
        tip: '付款讲者的照片（需备注姓名），照片清晰',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 4. 住宿费 — 按需展开
  // =====================================================
  {
    code: 'ACCOMMODATION',
    label: '住宿费',
    legacyCode: '2',
    icon: 'el-icon-house',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'HOTEL_DETAIL',
        label: '住宿明细表',
        required: true,
        accept: ACCEPT.PDF,
        tip: '入住明细汇总表：姓名、入住日期、天数、金额（盖章）',
        auditRules: [],
      },
      {
        id: 'HOTEL_INVOICE',
        label: '住宿发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（需盖章，清晰完整）',
        auditRules: ['INVOICE_AMOUNT_MATCH', 'INVOICE_SEAL'],
      },
      {
        id: 'HOTEL_RECEIPT',
        label: '住宿水单',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '水单名称与发票开票方需一致；不一致需提供关系证明',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 5. 餐费（含茶歇） — 按需展开
  // =====================================================
  {
    code: 'MEAL',
    label: '餐费（茶歇）',
    legacyCode: '3',
    icon: 'el-icon-food',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'MEAL_INVOICE',
        label: '餐费发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（需盖章，清晰完整）',
        auditRules: ['MEAL_COMPREHENSIVE', 'INVOICE_SEAL'],
      },
      {
        id: 'MEAL_RECEIPT',
        label: '餐费水单',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '水单需体现用餐时间、用餐人数、单价、金额',
        auditRules: ['MEAL_STANDARD', 'ATTENDANCE_MEAL_CROSS_CHECK'],
      },
      {
        id: 'TEA_INVOICE',
        label: '茶歇发票',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '如有茶歇费用，提供发票',
        auditRules: [],
      },
      {
        id: 'TEA_RECEIPT',
        label: '茶歇水单',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '不超过80元/人/天',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 6. 场租费 — 按需展开
  // =====================================================
  {
    code: 'VENUE',
    label: '场租费',
    legacyCode: '4',
    icon: 'el-icon-office-building',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'VENUE_INVOICE',
        label: '场租发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（需盖章，清晰完整）',
        auditRules: ['VENUE_RENT_COMPREHENSIVE', 'INVOICE_SEAL'],
      },
      {
        id: 'VENUE_RECEIPT',
        label: '场租水单',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '体现日期、单价、数量、金额；非正式水单需含面积、地址',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 7. 交通费 — 按需展开
  // =====================================================
  {
    code: 'TRANSPORT',
    label: '交通费（例如：滴滴出行）',
    legacyCode: '5',
    icon: 'el-icon-truck',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'TRANSPORT_DETAIL',
        label: '交通明细表',
        required: true,
        accept: ACCEPT.PDF,
        tip: '包含：人员姓名、日期、起止地点、金额（盖章）',
        auditRules: ['TRAVEL_COMPREHENSIVE'],
      },
      {
        id: 'TRANSPORT_INVOICE',
        label: '交通发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（需盖章，清晰完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'TRANSPORT_CONTRACT',
        label: '交通合同（第三方租车时）',
        required: false,
        accept: ACCEPT.PDF,
        tip: '若第三方提供租车，需提供发票+结算单/合同',
        auditRules: [],
      },
      {
        id: 'CAR_DETAIL',
        label: '租车费明细表',
        required: false,
        accept: ACCEPT.PDF,
        tip: '行程明细表需盖章',
        auditRules: [],
      },
      {
        id: 'CAR_INVOICE',
        label: '租车费发票',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '不可高于协议单价',
        auditRules: [],
      },
      {
        id: 'CAR_CONTRACT',
        label: '租车费合同',
        required: false,
        accept: ACCEPT.PDF,
        tip: '费用发生时间与会议时间匹配',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 8. 铁路交通 — 按需展开
  // =====================================================
  {
    code: 'RAIL',
    label: '铁路交通',
    legacyCode: '6',
    icon: 'el-icon-guide',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'RAIL_DETAIL',
        label: '火车票汇总明细表',
        required: true,
        accept: ACCEPT.PDF,
        tip: '包含：姓名、日期、车次、起止地点、金额及合计金额（原件）',
        auditRules: [],
      },
      {
        id: 'RAIL_TICKET',
        label: '火车票原件',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '火车票扫描件或照片',
        auditRules: [],
      },
      {
        id: 'RAIL_REFUND_INVOICE',
        label: '退票发票',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '退改签说明需盖章（原件）',
        auditRules: [],
      },
      {
        id: 'RAIL_REFUND_SCREENSHOT',
        label: '退改签截图',
        required: false,
        accept: ACCEPT.IMAGE,
        tip: '退改签操作截图',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 9. 机票费 — 按需展开
  // =====================================================
  {
    code: 'FLIGHT',
    label: '机票费',
    legacyCode: '7',
    icon: 'el-icon-position',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'FLIGHT_DETAIL',
        label: '机票汇总明细表',
        required: true,
        accept: ACCEPT.PDF,
        tip: '姓名、日期、航班号、起止地点、金额及合计金额',
        auditRules: [],
      },
      {
        id: 'FLIGHT_ITINERARY',
        label: '发票或行程单',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '行程单信息需清晰完整',
        auditRules: [],
      },
      {
        id: 'FLIGHT_INSURANCE',
        label: '保险费发票',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '产生保险费时提供，含保单',
        auditRules: [],
      },
      {
        id: 'FLIGHT_REFUND',
        label: '退票发票',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '退票、误机、变更收费单（加盖出票单位业务专用章）',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 10. 物料制作费 — 按需展开
  // =====================================================
  {
    code: 'MATERIAL',
    label: '物料制作费',
    legacyCode: '8',
    icon: 'el-icon-box',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'MATERIAL_PHOTO',
        label: '物料照片（现场发放）',
        required: true,
        accept: ACCEPT.IMAGE,
        tip: '物料照片清晰；物料内容与项目主题相关',
        auditRules: ['MATERIAL_COMPREHENSIVE'],
      },
      {
        id: 'MATERIAL_DIST_LIST',
        label: '物料发放明细清单（物料寄出时）',
        required: false,
        accept: ACCEPT.PDF,
        tip: '项目负责人签字验收版：快递单号、发货日期、收货地址、收件人、货物名称、数量、金额（盖章原件）',
        auditRules: [],
      },
      {
        id: 'MATERIAL_INVOICE',
        label: '物料发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（需盖章，清晰完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'MATERIAL_DETAIL',
        label: '物料明细清单/合同',
        required: true,
        accept: ACCEPT.PDF,
        tip: '结算清单/合同体现：物料名称、单价、数量、总金额',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 11. 直播/平台软件费用 — 按需展开
  // =====================================================
  {
    code: 'PLATFORM',
    label: '直播/系统平台软件费用',
    legacyCode: '9',
    icon: 'el-icon-monitor',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'PLATFORM_SCREENSHOT',
        label: '直播/平台软件截图',
        required: true,
        accept: ACCEPT.IMAGE,
        tip: '平台界面截图',
        auditRules: [],
      },
      {
        id: 'PLATFORM_INVOICE',
        label: '直播/平台发票',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '第三方提供时需要：发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'PLATFORM_CONTRACT',
        label: '直播/平台合同/结算单',
        required: false,
        accept: ACCEPT.PDF,
        tip: '协议（双方盖章）/ 结算单（盖章版复印件）；数据明细表盖章（原件）',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 12. 差旅费（打包报销） — 按需展开
  // =====================================================
  {
    code: 'TRAVEL_PACK',
    label: '差旅费（实报实销）',
    legacyCode: '10',
    icon: 'el-icon-suitcase',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'TRAVEL_DETAIL',
        label: '差旅费汇总明细表',
        required: true,
        accept: ACCEPT.PDF,
        tip: '包含：日期、费用名称、金额、合计金额',
        auditRules: [],
      },
      {
        id: 'TRAVEL_TRANSPORT_INVOICE',
        label: '交通费发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'TRAVEL_TRANSPORT_ITINERARY',
        label: '交通行程单',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '行程单与发票配套且金额一致',
        auditRules: [],
      },
      {
        id: 'TRAVEL_HOTEL_INVOICE',
        label: '住宿发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'TRAVEL_HOTEL_RECEIPT',
        label: '住宿水单',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '水单名称与发票开票方一致',
        auditRules: [],
      },
      {
        id: 'TRAVEL_MEAL_INVOICE',
        label: '餐费发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'TRAVEL_MEAL_RECEIPT',
        label: '餐费水单',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '水单名称与发票开票方一致',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 13. 撰写材料费 — 按需展开
  // =====================================================
  {
    code: 'WRITING',
    label: '材料撰写费（串场PPT、医学幻灯撰写）',
    legacyCode: '11',
    icon: 'el-icon-edit',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'WRITING_CONTENT',
        label: '撰写的材料文件',
        required: true,
        accept: ACCEPT.DOC,
        tip: '撰写材料需确保真实性，无敏感字眼、无商业内容（如药品广告等）',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 14. 执行人员服务费 — 按需展开
  // =====================================================
  {
    code: 'EXEC_SERVICE',
    label: '执行人员服务费',
    legacyCode: '12',
    icon: 'el-icon-user',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'EXEC_DETAIL',
        label: '执行人员明细表',
        required: true,
        accept: ACCEPT.PDF,
        tip: '明细表包含：姓名、职务（工作内容）、工作时间、天数、单价、合计金额（盖章原件）',
        auditRules: ['EXECUTION_STAFF_COMPREHENSIVE'],
      },
    ],
  },

  // =====================================================
  // 15. 平台宣传发布费 — 按需展开
  // =====================================================
  {
    code: 'PROMOTION',
    label: '平台内容宣传发布 / 软文排版 / 撰稿费',
    legacyCode: '13',
    icon: 'el-icon-star-on',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'PROMO_SCREENSHOT',
        label: '宣传发布截图/软文截图/版面截图',
        required: true,
        accept: ACCEPT.ALL,
        tip: '截图清晰，内容明确',
        auditRules: [],
      },
      {
        id: 'PROMO_INVOICE',
        label: '发票（第三方提供时）',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'PROMO_CONTRACT',
        label: '合同/结算单（第三方）',
        required: false,
        accept: ACCEPT.PDF,
        tip: '双方盖章复印件/盖章版复印件',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 16. 延展设计费 — 按需展开
  // =====================================================
  {
    code: 'DESIGN',
    label: '延展设计费（主KV、日程海报、邀请函、桌卡等）',
    legacyCode: '14',
    icon: 'el-icon-picture-outline',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'DESIGN_IMAGE',
        label: '设计图片',
        required: true,
        accept: ACCEPT.ALL, // 可能有PDF格式
        tip: '呈现设计结果图片；内容与项目主题相符',
        auditRules: ['DESIGN_COMPREHENSIVE'],
      },
    ],
  },

  // =====================================================
  // 17. 视频制作剪辑及脚本撰写费 — 按需展开
  // =====================================================
  {
    code: 'VIDEO',
    label: '视频制作剪辑及脚本撰写费',
    legacyCode: '15',
    icon: 'el-icon-video-camera',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'VIDEO_FILE',
        label: '视频/视频链接',
        required: true,
        accept: ACCEPT.VIDEO_LINK,
        tip: '长期有效的视频或视频链接',
        auditRules: [],
      },
      {
        id: 'VIDEO_SCRIPT',
        label: '视频脚本',
        required: true,
        accept: ACCEPT.DOC,
        tip: '视频脚本撰写内容',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 18. 场地搭建及设备租赁费 — 按需展开
  // =====================================================
  {
    code: 'STAGING',
    label: '场地搭建及设备租赁费',
    legacyCode: '16',
    icon: 'el-icon-set-up',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'STAGING_PHOTO',
        label: '场地搭建及设备租赁照片',
        required: true,
        accept: ACCEPT.IMAGE,
        tip: '照片清晰完整',
        auditRules: [],
      },
      {
        id: 'STAGING_INVOICE',
        label: '发票（第三方时）',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'STAGING_CONTRACT',
        label: '合同/结算单（第三方时）',
        required: false,
        accept: ACCEPT.PDF,
        tip: '双方盖章复印件/盖章版复印件',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 19. 搬运费 — 按需展开
  // =====================================================
  {
    code: 'PORTER',
    label: '搬运费',
    legacyCode: '17',
    icon: 'el-icon-rank',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'PORTER_PHOTO',
        label: '搬运照片',
        required: true,
        accept: ACCEPT.IMAGE,
        tip: '照片清晰完整',
        auditRules: [],
      },
      {
        id: 'PORTER_INVOICE',
        label: '搬运发票（第三方时）',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'PORTER_CONTRACT',
        label: '搬运合同/结算单（第三方时）',
        required: false,
        accept: ACCEPT.PDF,
        tip: '双方盖章复印件/盖章版复印件',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 20. 物流（快递）运输费 — 按需展开
  // =====================================================
  {
    code: 'LOGISTICS',
    label: '物流（快递）运输费',
    legacyCode: '18',
    icon: 'el-icon-van',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'LOGISTICS_DETAIL',
        label: '运输明细表',
        required: true,
        accept: ACCEPT.PDF,
        tip: '内容体现：时间、运输起止地点、数量、单价、总金额（盖章原件）',
        auditRules: [],
      },
      {
        id: 'LOGISTICS_INVOICE',
        label: '运输发票',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '电子/纸质增值税发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'LOGISTICS_RECEIPT',
        label: '运输底单',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '运输底单或运输明细表（盖章版复印件）',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 21. 培训费 — 按需展开
  // =====================================================
  {
    code: 'TRAINING',
    label: '培训费',
    legacyCode: '19',
    icon: 'el-icon-reading',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'TRAINING_NOTICE',
        label: '培训通知',
        required: true,
        accept: ACCEPT.PDF_IMAGE,
        tip: '体现培训时间、地点、主题',
        auditRules: [],
      },
      {
        id: 'TRAINING_SIGNIN',
        label: '培训签到表',
        required: true,
        accept: ACCEPT.PDF_EXCEL,
        tip: '体现培训时间、地点、主题、参训人员姓名',
        auditRules: [],
      },
      {
        id: 'TRAINING_COURSEWARE',
        label: '培训课件',
        required: true,
        accept: ACCEPT.DOC,
        tip: '提供PPT或文档（电子版）；内容与培训主题相符',
        auditRules: [],
      },
      {
        id: 'TRAINING_PHOTO',
        label: '培训现场照片/线上截图',
        required: true,
        accept: ACCEPT.ALL,
        minCount: 3,
        tip: '至少3张，包含培训主题背景、会场情况、参会者',
        auditRules: ['PHOTO_COUNT'],
      },
    ],
  },

  // =====================================================
  // 22. 咨询费 — 按需展开
  // =====================================================
  {
    code: 'CONSULTING',
    label: '咨询费',
    legacyCode: '20',
    icon: 'el-icon-chat-dot-round',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'CONSULTING_CONTENT',
        label: '咨询内容',
        required: true,
        accept: ACCEPT.DOC,
        tip: '咨询内容明确',
        auditRules: [],
      },
      {
        id: 'CONSULTING_PHOTO',
        label: '咨询过程照片',
        required: true,
        accept: ACCEPT.IMAGE,
        tip: '照片清晰完整',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 23. 摄影摄像 — 按需展开
  // =====================================================
  {
    code: 'PHOTOGRAPHY',
    label: '摄影摄像',
    legacyCode: '21',
    icon: 'el-icon-camera',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'PHOTO_STAFF_PHOTO',
        label: '摄影师及装备照片',
        required: true,
        accept: ACCEPT.IMAGE,
        tip: '摄影师现场工作照片、摄影装备照片清晰可见',
        auditRules: [],
      },
      {
        id: 'PHOTO_INVOICE',
        label: '摄影/摄像发票',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '第三方提供时需要发票（盖章完整）',
        auditRules: ['INVOICE_SEAL'],
      },
      {
        id: 'PHOTO_CONTRACT',
        label: '摄影/摄像合同/结算单',
        required: false,
        accept: ACCEPT.PDF,
        tip: '双方盖章复印件/盖章版复印件',
        auditRules: [],
      },
    ],
  },

  // =====================================================
  // 24. 其他/情况说明 — 按需展开
  // =====================================================
  {
    code: 'OTHER',
    label: '其他附件 / 情况说明',
    legacyCode: null,
    icon: 'el-icon-more',
    alwaysRequired: false,
    defaultExpanded: false,
    slots: [
      {
        id: 'OTHER_EXPLANATION',
        label: '情况说明',
        required: false,
        accept: ACCEPT.PDF,
        tip: '情况说明盖鲜章+项目负责人签字带日期。落款时间在费用期间之后。',
        auditRules: [],
      },
      {
        id: 'OTHER_REALISTIC_AMOUNT',
        label: '写实报金额说明',
        required: false,
        accept: ACCEPT.PDF,
        tip: '发票与水单金额不一致时需备注写实报金额',
        auditRules: [],
      },
      {
        id: 'OTHER_SUPPLIER_REVIEW',
        label: '供应商评审',
        required: false,
        accept: ACCEPT.PDF_IMAGE,
        tip: '一次性结算或尾款结算需供应商评审分数',
        auditRules: [],
      },
    ],
  },
]

// ========================
// 工具函数
// ========================

/**
 * 通过 legacyCode (老系统 spendingProjectCode) 查找费用大类
 */
export function getCategoryByLegacyCode(code) {
  return FEE_CATEGORIES.find(c => c.legacyCode === code)
}

/**
 * 通过新系统 code 查找费用大类
 */
export function getCategoryByCode(code) {
  return FEE_CATEGORIES.find(c => c.code === code)
}

/**
 * 获取所有"每单必传"的费用大类
 */
export function getAlwaysRequiredCategories() {
  return FEE_CATEGORIES.filter(c => c.alwaysRequired)
}

/**
 * 获取所有"按需展开"的费用大类（用于填充支出明细下拉框）
 */
export function getOptionalCategories() {
  return FEE_CATEGORIES.filter(c => !c.alwaysRequired)
}

/**
 * 根据已选择的费用类别编码列表，返回需要展示的所有费用大类（含必传 + 已选）
 */
export function getActiveCategories(selectedCodes = []) {
  return FEE_CATEGORIES.filter(c => c.alwaysRequired || selectedCodes.includes(c.code))
}

/**
 * 检查某个费用大类下的必填槽是否都有文件
 * @param {object} category - 费用大类配置
 * @param {object} filesMap - { slotId: [file1, file2, ...] }
 * @param {object} conditions - { meetingMode: 'ONLINE'|'OFFLINE' }
 * @returns {{ complete: boolean, missing: string[] }}
 */
export function validateCategory(category, filesMap = {}, conditions = {}) {
  const missing = []
  for (const slot of category.slots) {
    // 检查条件限制
    if (slot.conditions) {
      const shouldShow = Object.entries(slot.conditions).every(
        ([key, val]) => conditions[key] === val
      )
      if (!shouldShow) continue // 不满足条件的槽位跳过校验
    }
    if (slot.required) {
      const files = filesMap[slot.id] || []
      if (files.length === 0) {
        missing.push(slot.label)
      }
      // 检查最小文件数
      if (slot.minCount && files.length < slot.minCount) {
        missing.push(`${slot.label}（至少${slot.minCount}个文件，当前${files.length}个）`)
      }
    }
  }
  return { complete: missing.length === 0, missing }
}

/**
 * 新旧费用编码映射表（用于数据迁移和兼容）
 */
export const LEGACY_CODE_MAP = {
  'UM000': 'GENERAL',  // 综合材料（含基础资料）
  '1': 'LABOR_FEE',    // 劳务费
  '2': 'ACCOMMODATION',// 住宿费
  '3': 'MEAL',         // 餐费
  '4': 'VENUE',        // 场租费
  '5': 'TRANSPORT',    // 交通费
  '6': 'RAIL',         // 铁路交通
  '7': 'FLIGHT',       // 机票费
  '8': 'MATERIAL',     // 物料费
  '9': 'PLATFORM',     // 平台软件费
  '10': 'TRAVEL_PACK', // 差旅费
  '11': 'WRITING',     // 撰写材料费
  '12': 'EXEC_SERVICE',// 执行人员服务费
  '13': 'PROMOTION',   // 平台宣传发布费
  '14': 'DESIGN',      // 延展设计费
  '15': 'VIDEO',       // 视频制作费
  '16': 'STAGING',     // 场地搭建费
  '17': 'PORTER',      // 搬运费
  '18': 'LOGISTICS',   // 物流运输费
  '19': 'TRAINING',    // 培训费
  '20': 'CONSULTING',  // 咨询费
  '21': 'PHOTOGRAPHY', // 摄影摄像
}
